/**
 * CruisePrice.tsx — クルーズ船料金表（宮古だけ・/cruise）（2026-10-22）
 *
 * 車種を選ぶと、その月のカレンダーに backend のカレンダー料金（vehicle-type-prices-v2）を表示し、
 * 日ごとにクルーズ料金を入力・保存できる。クルーズ料金は平良港を送迎場所に選んだ予約だけに使い、
 * backend の料金より優先する（LP の予約フォームと calc API が読む）。
 * 那覇は「料金の種類」でクルーズ船プラン（①朝着〜④1泊。2026-11-02）を選ぶと、プラン × 車種 × 入港日の料金を同じカレンダーで入力できる。
 * 「寄港予定」タブ（2026-10-31）: 沖縄県オープンデータから取り込んだ寄港予定を確かめて公開する（components/cruise/CruisePortCallsPanel）。
 * 正: booking-platform/md/pricing.md「クルーズ船料金表」 / ページ仕様: md/master_md/page-cruise.md
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Button, IconButton, TextField, MenuItem, Alert, Tooltip, Tabs, Tab,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import DashboardSidebar from '../components/DashboardSidebar';
import CruisePortCallsPanel from '../components/cruise/CruisePortCallsPanel';
import { fetchVehicleTypePricesV2 } from '../api/dispatchService';
import { logEvent } from '../lib/eventLog';
import {
  CRUISE_PLANS, CRUISE_PLAN_VEHICLES, CRUISE_STORE_INFO, cruiseStore, buildMonthWeeks, diffDraft, fetchCruisePriceTables, fillRange,
  hasCruisePlans, parsePriceInput, saveCruisePrices, toDraft,
  type CruisePlanKey, type DayPrices, type DraftPrices, type PlanPrices,
} from '../lib/cruisePrices';

interface VehicleTypePrices {
  id: number | string;
  code: string;
  name: string;
  base_price: number;
  prices: DayPrices;
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const yen = (n: number) => `¥${n.toLocaleString()}`;

type CruiseTab = 'prices' | 'ports';
/** 料金の種類: 'day' = 日別のクルーズ料金（日時で選ぶ予約）／プラン = 那覇のクルーズ船プラン（船を選んだ予約） */
type PriceKind = 'day' | CruisePlanKey;
const initialTab = (): CruiseTab => {
  try { return new URLSearchParams(window.location.search).get('tab') === 'ports' ? 'ports' : 'prices'; } catch { return 'prices'; }
};

const CruisePrice: React.FC = () => {
  const [tab, setTab] = useState<CruiseTab>(initialTab);
  const store = cruiseStore();
  const info = CRUISE_STORE_INFO[store];
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [types, setTypes] = useState<VehicleTypePrices[]>([]);
  const [code, setCode] = useState('');
  const [cruiseAll, setCruiseAll] = useState<Record<string, DayPrices>>({});
  const [planAll, setPlanAll] = useState<PlanPrices>({});
  const [kind, setKind] = useState<PriceKind>('day');
  const plansEnabled = hasCruisePlans(store);
  const planKey = kind === 'day' ? undefined : kind;
  const planInfo = CRUISE_PLANS.find(p => p.key === kind) || null;
  const [draft, setDraft] = useState<DraftPrices>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [bulk, setBulk] = useState({ from: '', to: '', value: '' });

  const from = `${month}-01`;
  const to = dayjs(from).endOf('month').format('YYYY-MM-DD');
  const saved = useMemo(
    () => (planKey ? planAll[planKey]?.[code] : cruiseAll[code]) || {},
    [cruiseAll, planAll, planKey, code],
  );
  // プランは対象の 7 車種だけ（backend が返す順のまま）
  const visibleTypes = useMemo(
    () => (planKey ? types.filter(t => CRUISE_PLAN_VEHICLES.includes(t.code)) : types),
    [types, planKey],
  );
  const selected = visibleTypes.find(t => t.code === code) || null;
  const weeks = useMemo(() => buildMonthWeeks(month), [month]);
  const { items: changes, invalid } = useMemo(() => diffDraft(code, saved, draft, planKey), [code, saved, draft, planKey]);
  const dirty = changes.length > 0 || invalid.length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [priceResp, tables] = await Promise.all([
        fetchVehicleTypePricesV2({ start_date: from, end_date: to }),
        fetchCruisePriceTables(from, to),
      ]);
      const list: VehicleTypePrices[] = (priceResp?.data?.vehicle_types || [])
        .filter((vt: any) => vt?.code)
        .map((vt: any) => ({ id: vt.id, code: vt.code, name: vt.name || vt.code, base_price: Number(vt.base_price) || 0, prices: vt.prices || {} }));
      if (list.length === 0) throw new Error('車種と料金を backend から取得できませんでした');
      setTypes(list);
      setCruiseAll(tables.prices);
      setPlanAll(tables.planPrices);
      setCode(prev => (prev && list.some(t => t.code === prev) ? prev : list[0].code));
    } catch (e) {
      setError(`読み込みに失敗しました: ${(e as Error).message}`);
      logEvent({ action: 'cruise_price_load', target: month, result: 'fail', err: e });
    } finally {
      setLoading(false);
    }
  }, [from, to, month]);

  useEffect(() => { load(); }, [load]);
  // 車種・月・料金の種類・保存済みの値が変わったら下書きを作り直す
  useEffect(() => { setDraft(toDraft(saved)); }, [saved, code, month, kind]);
  // プランに切り替えて今の車種が対象外なら、対象の先頭の車種にする
  useEffect(() => {
    if (visibleTypes.length > 0 && !visibleTypes.some(t => t.code === code)) setCode(visibleTypes[0].code);
  }, [visibleTypes, code]);

  const confirmDiscard = () => !dirty || window.confirm('保存していない変更があります。破棄しますか？');

  const moveMonth = (delta: number) => {
    if (!confirmDiscard()) return;
    setNotice(null);
    setMonth(dayjs(`${month}-01`).add(delta, 'month').format('YYYY-MM'));
  };

  const changeKind = (next: PriceKind) => {
    if (!confirmDiscard()) return;
    setNotice(null);
    setKind(next);
  };

  const changeCode = (next: string) => {
    if (!confirmDiscard()) return;
    setNotice(null);
    setCode(next);
  };

  const handleSave = async () => {
    if (invalid.length > 0 || changes.length === 0) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const r = await saveCruisePrices(changes);
      logEvent({ action: 'cruise_price_save', target: `${planKey ? `${planKey} ` : ''}${code} ${month}`, result: 'ok' });
      setNotice(`保存しました（${planInfo ? `${planInfo.label}・` : ''}設定 ${r.saved} 日・解除 ${r.deleted} 日）`);
      const tables = await fetchCruisePriceTables(from, to);
      setCruiseAll(tables.prices);
      setPlanAll(tables.planPrices);
    } catch (e) {
      setError(`保存に失敗しました: ${(e as Error).message}`);
      logEvent({ action: 'cruise_price_save', target: `${planKey ? `${planKey} ` : ''}${code} ${month}`, result: 'fail', err: e });
    } finally {
      setSaving(false);
    }
  };

  const applyBulk = () => {
    const f = bulk.from || from;
    const t = bulk.to || to;
    setDraft(prev => fillRange(prev, f < from ? from : f, t > to ? to : t, bulk.value));
  };

  const setCount = Object.keys(saved).length;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardSidebar />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 }, overflow: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>クルーズ船</Typography>
          <Tabs value={tab} sx={{ mb: 1.5 }} onChange={(_, v: CruiseTab) => {
            if (v === tab || (tab === 'prices' && !confirmDiscard())) return;
            setTab(v);
            try { window.history.replaceState(null, '', v === 'ports' ? '?tab=ports' : window.location.pathname); } catch { /* 表示だけ */ }
          }}>
            <Tab value="prices" label="料金表" />
            <Tab value="ports" label="寄港予定" />
          </Tabs>
          {tab === 'prices' && !planInfo && <Typography variant="body2" sx={{ color: '#64748b' }}>
            クルーズ船の予約（{info.port}）だけに使う1日あたりの料金です。入っている日は backend の料金より優先し、
            送迎料（¥{info.surcharge.toLocaleString()}）は込みになります。空欄の日は通常の料金＋送迎料です。補償（CDW・NOC）はこの金額に足して「込み」と表示します。
            {plansEnabled && '寄港予定で船を選んだ予約は、下の「料金の種類」で選べるクルーズ船プランの料金を使います。'}
          </Typography>}
          {tab === 'prices' && planInfo && <Typography variant="body2" sx={{ color: '#64748b' }}>
            クルーズ船プラン {planInfo.label}（{planInfo.hint}）の料金です。<b>寄港予定で船を選んだ予約だけ</b>に使い、日付は<b>入港日</b>です。
            この金額に CDW・NOC・港の送迎料（¥{info.surcharge.toLocaleString()}）・給油・充電が含まれます（お客様には1行の金額だけ見せます）。
            空欄の日は、船を選んでも日別の計算（上の「日別」）になります。
          </Typography>}
        </Box>

        {tab === 'ports' ? <CruisePortCallsPanel store={store} /> : (<>

        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {plansEnabled && (
            <TextField
              select size="small" label="料金の種類" value={kind} sx={{ minWidth: 200 }}
              onChange={e => changeKind(e.target.value as PriceKind)}
            >
              <MenuItem value="day">日別（日時で選ぶ予約）</MenuItem>
              {CRUISE_PLANS.map(p => (
                <MenuItem key={p.key} value={p.key}>プラン {p.label}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select size="small" label="車種" value={selected ? code : ''} sx={{ minWidth: 220 }}
            onChange={e => changeCode(e.target.value)} disabled={visibleTypes.length === 0}
          >
            {visibleTypes.map(t => (
              <MenuItem key={t.code} value={t.code}>{t.name}（{t.code}）</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => moveMonth(-1)} aria-label="前の月"><ChevronLeftIcon /></IconButton>
            <Typography sx={{ fontWeight: 700, minWidth: 96, textAlign: 'center' }}>{dayjs(from).format('YYYY年M月')}</Typography>
            <IconButton onClick={() => moveMonth(1)} aria-label="次の月"><ChevronRightIcon /></IconButton>
          </Box>
          <Tooltip title="再読み込み">
            <span><IconButton onClick={() => { if (confirmDiscard()) load(); }} disabled={loading}><RefreshIcon /></IconButton></span>
          </Tooltip>
          {selected && (
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {planInfo ? `プラン ${planInfo.label}` : `標準価格 ${yen(selected.base_price)}`}・この月の設定 {setCount} 日
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={handleSave} disabled={saving || loading || changes.length === 0 || invalid.length > 0}>
            {saving ? '保存中…' : `保存${changes.length > 0 ? `（${changes.length} 日）` : ''}`}
          </Button>
        </Paper>

        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>まとめて入力</Typography>
          <TextField type="date" size="small" label="開始日" value={bulk.from || from} InputLabelProps={{ shrink: true }}
            inputProps={{ min: from, max: to }} onChange={e => setBulk(b => ({ ...b, from: e.target.value }))} />
          <TextField type="date" size="small" label="終了日" value={bulk.to || to} InputLabelProps={{ shrink: true }}
            inputProps={{ min: from, max: to }} onChange={e => setBulk(b => ({ ...b, to: e.target.value }))} />
          <TextField size="small" label="金額（空欄＝解除）" value={bulk.value} sx={{ width: 160 }}
            onChange={e => setBulk(b => ({ ...b, value: e.target.value }))} />
          <Button variant="outlined" onClick={applyBulk} disabled={!code}>入力欄に反映</Button>
          <Typography variant="caption" sx={{ color: '#64748b' }}>反映したあと「保存」で確定します</Typography>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
        {invalid.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            金額は 1〜1,000,000 の整数で入れてください（{invalid.map(d => dayjs(d).format('M/D')).join('・')}）
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : selected && (
          <Paper sx={{ p: { xs: 1, md: 2 }, overflowX: 'auto' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(104px, 1fr))', gap: 1, minWidth: 760 }}>
              {WEEKDAYS.map((w, i) => (
                <Typography key={w} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: i === 0 ? '#dc2626' : i === 6 ? '#2563eb' : '#475569' }}>{w}</Typography>
              ))}
              {weeks.flat().map((date, idx) => {
                if (!date) return <Box key={`e${idx}`} />;
                const backendPrice = selected.prices[date];
                const raw = draft[date] ?? '';
                const parsed = parsePriceInput(raw);
                const changed = parsed !== undefined && parsed !== (saved[date] ?? null);
                const bad = parsed === undefined;
                const hasCruise = parsed !== null && parsed !== undefined;
                return (
                  <Box key={date} sx={{
                    border: '1px solid', borderRadius: 1, p: 1,
                    borderColor: bad ? '#dc2626' : changed ? '#f59e0b' : hasCruise ? '#0ea5e9' : '#e2e8f0',
                    background: hasCruise ? '#f0f9ff' : '#fff',
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{dayjs(date).date()}</Typography>
                    {planInfo ? (
                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                        {hasCruise ? '込み（補償・送迎・給油）' : '未設定＝日別の計算'}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b', textDecoration: hasCruise ? 'line-through' : 'none' }}>
                        通常 {yen(backendPrice ?? selected.base_price)}{backendPrice == null ? '（標準）' : ''}
                      </Typography>
                    )}
                    <TextField
                      size="small" fullWidth placeholder={planInfo ? planInfo.label : 'クルーズ'} value={raw} error={bad}
                      inputProps={{ inputMode: 'numeric', 'aria-label': `${date} の${planInfo ? `プラン${planInfo.label}` : 'クルーズ'}料金` }}
                      onChange={e => setDraft(prev => ({ ...prev, [date]: e.target.value }))}
                      sx={{ mt: 0.5, '& input': { py: 0.5, fontSize: 13 } }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}
        </>)}
      </Box>
    </Box>
  );
};

export default CruisePrice;
